import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
	Modal as ChakraModal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	HStack,
	Text,
} from "@chakra-ui/react";
import { FiSettings } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { exportPdf } from "newui/features/Settings/PdfSettings/api/design-schema-api";
import { useGlobalDesignSchema } from "newui/features/Settings/PdfSettings/hooks/useDesignSchema";
import { downloadFileNoPopup } from "helpers/linkHelper";
import { useShortlistReportState } from "../hooks/useShortlistReportState";
import type { ShortlistReportContext } from "../types";
import SectorFilterPanel from "./panels/SectorFilterPanel";
import ProjectAllocationPanel from "./panels/ProjectAllocationPanel";
import AdditionalTextPanel from "./panels/AdditionalTextPanel";
import SectionVisibilityPanel from "./panels/SectionVisibilityPanel";
import { ShortlistPreviewPanel } from "./panels/ShortlistPreviewPanel";
import { CoverPagePanel } from "./panels/CoverPagePanel";
import { EditCoverPageDrawer } from "./EditCoverPageDrawer";
import PresetPanel from "./panels/PresetPanel";

interface CustomiseReportModalProps {
	context: ShortlistReportContext;
	currentCompanyId: number;
	isOpen: boolean;
	onClose: () => void;
}

const BREAKPOINT = 1024;

export const CustomiseReportModal: React.FC<CustomiseReportModalProps> = ({
	isOpen,
	onClose,
	context,
	currentCompanyId,
}) => {
	const schemaQuery = useGlobalDesignSchema(currentCompanyId);
	const enrichedContext = useMemo(() => {
		const schema = schemaQuery.data;
		if (!schema) return context;
		const raw = schema as unknown as Record<string, unknown>;
		return {
			...context,
			defaultSections: (raw.shortlist_visible_sections as Record<string, boolean>) ?? undefined,
			defaultFields: (raw.shortlist_visible_fields as Record<string, Record<string, boolean>>) ?? undefined,
		};
	}, [context, schemaQuery.data]);

	const {
		state,
		toggleSection,
		toggleField,
		setProject,
		setProjectDescription,
		setIncludeProjectPage,
		setAdditionalText,
		setIncludeAdditionalText,
		setCoverPageMode,
		setCoverPage,
		setCoverPageFileId,
		setProjectVisibleFields,
		setSections,
		setFields,
		setSectorIds,
		setCoverProjectName,
		setCoverSupplierRole,
		setCoverPresentedTo,
		setCoverPresentedToRole,
		setCoverShowLogo,
		setCoverCreatedByRole,
		loadPreset,
		resetToDefaults,
		visibleSectionCount,
	} = useShortlistReportState(enrichedContext);

	const [isExporting, setIsExporting] = useState(false);
	const [isNarrow, setIsNarrow] = useState(window.innerWidth < BREAKPOINT);
	const [activeTab, setActiveTab] = useState<"controls" | "preview">("controls");
	const [isCoverDrawerOpen, setIsCoverDrawerOpen] = useState(false);

	const globalShowLogo = useMemo(() => {
		const raw = schemaQuery.data as unknown as Record<string, unknown> | undefined;
		if (!raw) {
			return true;
		}
		return raw.cover_show_logo !== false;
	}, [schemaQuery.data]);

	useEffect(() => {
		const handleResize = () => setIsNarrow(window.innerWidth < BREAKPOINT);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const handleGeneratePdf = useCallback(async () => {
		setIsExporting(true);
		try {
			const result = await exportPdf(
				currentCompanyId,
				context.companyIds,
				undefined,
				state.sectorIds.length > 0 ? state.sectorIds : context.searchSectorIds,
				context.groupId,
				{
					visibleSections: Object.keys(state.sections).length > 0 ? state.sections : undefined,
					visibleFields: Object.keys(state.fields).length > 0 ? state.fields : undefined,
					projectId: state.projectId,
					projectDescriptionOverride: state.projectDescription || undefined,
					additionalText: state.includeAdditionalText ? state.additionalText || undefined : undefined,
					includeProjectDetails: state.includeProjectPage,
					coverPageMode: state.coverPageMode !== "global" ? state.coverPageMode : undefined,
					coverPageFileId: state.coverPageMode === "upload" ? state.coverPageFileId : undefined,
					projectVisibleFields: state.includeProjectPage ? state.projectVisibleFields : undefined,
					coverProjectName: state.coverProjectName || undefined,
					coverSupplierRole: state.coverSupplierRole || undefined,
					coverPresentedTo: state.coverPresentedTo || undefined,
					coverPresentedToRole: state.coverPresentedToRole || undefined,
					coverShowLogo: state.coverShowLogo,
					coverCreatedByRole: state.coverCreatedByRole || undefined,
				}
			);
			if (result?.download_url) {
				downloadFileNoPopup(
					result.download_url,
					result.filename || "shortlist-report.pdf",
					false
				);
			}
			onClose();
		} catch (error) {
			console.error("Failed to generate PDF:", error);
		} finally {
			setIsExporting(false);
		}
	}, [currentCompanyId, context.companyIds, context.groupId, state, onClose, context.searchSectorIds]);

	return (
		<ChakraModal
			isOpen={isOpen}
			onClose={onClose}
			size="full"
			scrollBehavior="inside"
			closeOnOverlayClick={false}
		>
			<ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
			<ModalContent
				maxH="95vh"
				display="flex"
				flexDirection="column"
				borderRadius="xl"
				mx={4}
				my={4}
			>
				<ModalCloseButton className="z-[999]" size="md" />
				<ModalHeader borderBottom="1px solid" borderColor="gray.100">
					<HStack spacing={3} align="center">
						<FiSettings size={24} className="text-blue-500" />
						<Text fontSize="xl" fontWeight="bold">
							Customise Shortlist Report
						</Text>
						<Text fontSize="sm" fontWeight="normal" color="gray.500" ml={2}>
							{context.companyIds.length} suppliers selected
						</Text>
					</HStack>
				</ModalHeader>

				<ModalBody p={0} flex={1} overflow="hidden" display="flex" flexDirection="column">
					{/* Responsive tab switcher for narrow screens */}
					{isNarrow && (
						<div className="flex shrink-0 gap-1 border-b border-gray-100 px-4 pt-3 pb-2">
							<button
								type="button"
								onClick={() => setActiveTab("controls")}
								className={`px-4 py-1.5 text-sm rounded-md ${activeTab === "controls"
									? "bg-blue-600 text-white"
									: "bg-gray-100 text-gray-600"
									}`}
							>
								Settings
							</button>
							<button
								type="button"
								onClick={() => setActiveTab("preview")}
								className={`px-4 py-1.5 text-sm rounded-md ${activeTab === "preview"
									? "bg-blue-600 text-white"
									: "bg-gray-100 text-gray-600"
									}`}
							>
								Preview
							</button>
						</div>
					)}

					<div className="flex min-h-0 flex-1">
						{/* Left Panel — Customisation Controls */}
						{(!isNarrow || activeTab === "controls") && (
							<div
								className={`${isNarrow ? "w-full" : "w-[420px] shrink-0 border-r border-gray-100"
									} overflow-y-auto p-6`}
							>
								<div className="flex flex-col gap-6">

									{/* Saved Presets */}
									<PresetPanel
										state={state}
										onLoadPreset={loadPreset}
										onSetSections={setSections}
										onSetFields={setFields}
									/>

									{/* Project Allocation — top per user request */}
									<ProjectAllocationPanel
										projectId={state.projectId}
										projectDescription={state.projectDescription}
										includeProjectPage={state.includeProjectPage}
										onProjectChange={setProject}
										onProjectDescriptionChange={setProjectDescription}
										onIncludeProjectPageChange={setIncludeProjectPage}
										projectVisibleFields={state.projectVisibleFields}
										onProjectVisibleFieldsChange={setProjectVisibleFields}
									/>

									{/* Cover Page */}
									<CoverPagePanel
										mode={state.coverPageMode}
										globalMode={schemaQuery.data?.cover_page_mode ?? undefined}
										onModeChange={setCoverPageMode}
										coverPageFile={state.coverPageFile}
										coverPagePreviewUrl={state.coverPagePreviewUrl}
										onCoverPageChange={setCoverPage}
										coverPageFileId={state.coverPageFileId}
										onCoverPageFileIdChange={setCoverPageFileId}
										onEditCoverPage={() => setIsCoverDrawerOpen(true)}
									/>

									{/* Additional Text Page */}
									<AdditionalTextPanel
										additionalText={state.additionalText}
										includeAdditionalText={state.includeAdditionalText}
										onAdditionalTextChange={setAdditionalText}
										onIncludeAdditionalTextChange={setIncludeAdditionalText}
									/>

									{/* Sector Filter */}
									<SectorFilterPanel
										sectorIds={state.sectorIds}
										ingress={context.ingress}
										onSectorIdsChange={setSectorIds}
									/>

									{/* Section & Field Visibility Controls */}
									<SectionVisibilityPanel
										sections={state.sections}
										fields={state.fields}
										onToggleSection={toggleSection}
										onToggleField={toggleField}
										onInitSections={setSections}
										onInitFields={setFields}
										visibleSectionCount={visibleSectionCount}
										onReset={resetToDefaults}
									/>
								</div>
							</div>
						)}

						{/* Right Panel — Live Preview */}
						{(!isNarrow || activeTab === "preview") && (
							<div className={`${isNarrow ? "w-full" : "flex-1"} overflow-y-auto bg-gray-50 px-6`}>
								<ShortlistPreviewPanel
									sections={state.sections}
									fields={state.fields}
									companyIds={context.companyIds}
									searchSectorIds={state.sectorIds.length > 0 ? state.sectorIds : context.searchSectorIds}
									groupId={context.groupId}
									additionalText={state.additionalText}
									includeAdditionalText={state.includeAdditionalText}
									projectId={state.projectId}
									projectDescription={state.projectDescription}
									includeProjectPage={state.includeProjectPage}
									projectVisibleFields={state.projectVisibleFields}
									coverProjectName={state.coverProjectName}
									coverSupplierRole={state.coverSupplierRole}
									coverPresentedTo={state.coverPresentedTo}
									coverPresentedToRole={state.coverPresentedToRole}
									coverShowLogo={state.coverShowLogo}
									coverCreatedByRole={state.coverCreatedByRole}
									coverPageMode={state.coverPageMode}
								/>
							</div>
						)}
					</div>
				</ModalBody>

				{/* Footer — Generate PDF */}
				<div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
					<Button
						btntype="secondary"
						size="md"
						onClick={onClose}
						disabled={isExporting}
					>
						Cancel
					</Button>
					<Button
						btntype="primary"
						size="md"
						onClick={handleGeneratePdf}
						isLoading={isExporting}
						loadingText="Generating PDF..."
					>
						Generate PDF
					</Button>
				</div>
			</ModalContent>

			<EditCoverPageDrawer
				isOpen={isCoverDrawerOpen}
				onClose={() => setIsCoverDrawerOpen(false)}
				coverProjectName={state.coverProjectName}
				coverSupplierRole={state.coverSupplierRole}
				coverPresentedTo={state.coverPresentedTo}
				coverPresentedToRole={state.coverPresentedToRole}
				coverShowLogo={state.coverShowLogo}
				coverCreatedByRole={state.coverCreatedByRole}
				globalShowLogo={globalShowLogo}
				onCoverProjectNameChange={setCoverProjectName}
				onCoverSupplierRoleChange={setCoverSupplierRole}
				onCoverPresentedToChange={setCoverPresentedTo}
				onCoverPresentedToRoleChange={setCoverPresentedToRole}
				onCoverShowLogoChange={setCoverShowLogo}
				onCoverCreatedByRoleChange={setCoverCreatedByRole}
			/>
		</ChakraModal>
	);
};

